import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, Shield, AlertTriangle, TrendingUp, TrendingDown,
  Users, ChevronRight, RefreshCw, Target, CheckCircle2,
  Clock, Ticket, GraduationCap, HardDrive, Search, Filter,
  BarChart3, AlertCircle, Eye, FileText
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MSPPortfolio = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [portfolio, setPortfolio] = useState(null);
  const [clients, setClients] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRisk, setFilterRisk] = useState('all');
  const [activeTab, setActiveTab] = useState('overview');
  const [error, setError] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('cset_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  useEffect(() => {
    fetchPortfolioData();
  }, []);

  const fetchPortfolioData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [overviewRes, alertsRes] = await Promise.all([
        fetch(`${API_URL}/api/msp/portfolio/overview`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/msp/portfolio/alerts`, { headers: getAuthHeaders() })
      ]);

      if (!overviewRes.ok) throw new Error('Failed to fetch portfolio data');

      const overviewData = await overviewRes.json();
      setPortfolio(overviewData.portfolio);
      setClients(overviewData.clients || []);

      if (alertsRes.ok) {
        const alertsData = await alertsRes.json();
        setAlerts(alertsData.alerts || []);
      }
    } catch (err) {
      setError('Failed to load portfolio data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getRiskColor = (level) => {
    switch(level) {
      case 'low': return 'emerald';
      case 'medium': return 'amber';
      case 'high': return 'orange';
      case 'critical': return 'red';
      default: return 'slate';
    }
  };

  const getRiskBg = (level) => {
    switch(level) {
      case 'low': return 'bg-emerald-500/20 border-emerald-500/30';
      case 'medium': return 'bg-amber-500/20 border-amber-500/30';
      case 'high': return 'bg-orange-500/20 border-orange-500/30';
      case 'critical': return 'bg-red-500/20 border-red-500/30';
      default: return 'bg-slate-500/20 border-slate-500/30';
    }
  };

  const filteredClients = clients.filter(client => {
    const matchesSearch = client.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRisk = filterRisk === 'all' || client.riskLevel === filterRisk;
    return matchesSearch && matchesRisk;
  });

  const StatCard = ({ icon: Icon, label, value, subValue, color = 'violet' }) => (
    <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
      <div className="flex items-center gap-3 mb-2">
        <div className={`w-10 h-10 rounded-lg bg-${color}-500/20 flex items-center justify-center`}>
          <Icon className={`w-5 h-5 text-${color}-400`} />
        </div>
      </div>
      <p className="text-2xl font-bold text-white">{value}</p>
      <p className="text-sm text-slate-400">{label}</p>
      {subValue && <p className="text-xs text-slate-500 mt-1">{subValue}</p>}
    </div>
  );

  const ClientCard = ({ client }) => {
    const riskColor = getRiskColor(client.riskLevel);
    
    return (
      <div 
        className={`bg-slate-800/50 rounded-xl p-5 border ${getRiskBg(client.riskLevel)} hover:bg-slate-800/70 transition-all cursor-pointer group`}
        onClick={() => navigate(`/msp/client/${client.id}`)}
        data-testid={`client-card-${client.id}`}
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl bg-${riskColor}-500/20 flex items-center justify-center`}>
              <Building2 className={`w-6 h-6 text-${riskColor}-400`} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white group-hover:text-violet-300 transition-colors">
                {client.name}
              </h3>
              <p className="text-sm text-slate-400">{client.totalAssessments} assessments</p>
            </div>
          </div>
          <div className={`px-3 py-1 rounded-full text-xs font-medium bg-${riskColor}-500/20 text-${riskColor}-400 border border-${riskColor}-500/30`}>
            {client.riskLabel}
          </div>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="bg-slate-900/50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <Shield className="w-4 h-4 text-violet-400" />
              <span className="text-xs text-slate-400">Compliance</span>
            </div>
            <p className={`text-lg font-bold ${client.complianceScore >= 70 ? 'text-emerald-400' : client.complianceScore >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
              {client.complianceScore}%
            </p>
          </div>
          <div className="bg-slate-900/50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="w-4 h-4 text-amber-400" />
              <span className="text-xs text-slate-400">Open Vulns</span>
            </div>
            <p className={`text-lg font-bold ${client.openVulnerabilities === 0 ? 'text-emerald-400' : client.openVulnerabilities > 50 ? 'text-red-400' : 'text-amber-400'}`}>
              {client.openVulnerabilities}
            </p>
          </div>
          <div className="bg-slate-900/50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <Ticket className="w-4 h-4 text-cyan-400" />
              <span className="text-xs text-slate-400">SLA Rate</span>
            </div>
            <p className={`text-lg font-bold ${client.slaAttainmentRate >= 90 ? 'text-emerald-400' : client.slaAttainmentRate >= 70 ? 'text-amber-400' : 'text-red-400'}`}>
              {client.slaAttainmentRate}%
            </p>
          </div>
          <div className="bg-slate-900/50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <GraduationCap className="w-4 h-4 text-emerald-400" />
              <span className="text-xs text-slate-400">Training</span>
            </div>
            <p className={`text-lg font-bold ${client.trainingCompletionRate >= 80 ? 'text-emerald-400' : client.trainingCompletionRate >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
              {client.trainingCompletionRate}%
            </p>
          </div>
        </div>

        {/* P1 Alert Warning */}
        {client.p1Vulnerabilities > 0 && (
          <div className="flex items-center gap-2 px-3 py-2 bg-red-500/10 border border-red-500/30 rounded-lg">
            <AlertCircle className="w-4 h-4 text-red-400" />
            <span className="text-sm text-red-400">{client.p1Vulnerabilities} P1 critical vulnerabilities</span>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-700/50">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Clock className="w-3 h-3" />
            {client.lastActivity ? `Last activity: ${new Date(client.lastActivity).toLocaleDateString()}` : 'No recent activity'}
          </div>
          <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-violet-400 transition-colors" />
        </div>
      </div>
    );
  };

  const AlertItem = ({ alert }) => (
    <div className={`flex items-center gap-4 p-4 rounded-lg ${alert.isOverdue ? 'bg-red-500/10 border border-red-500/30' : 'bg-slate-800/50 border border-slate-700/50'}`}>
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
        alert.type === 'vulnerability' ? 'bg-red-500/20' : 'bg-amber-500/20'
      }`}>
        {alert.type === 'vulnerability' ? (
          <AlertTriangle className="w-5 h-5 text-red-400" />
        ) : (
          <Ticket className="w-5 h-5 text-amber-400" />
        )}
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
            alert.priority === 'P1' ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'
          }`}>
            {alert.priority}
          </span>
          <span className="text-sm font-medium text-white">{alert.clientName}</span>
        </div>
        <p className="text-sm text-slate-300 mt-1">{alert.title}</p>
        <p className="text-xs text-slate-500 mt-1">Due: {alert.dueDate}</p>
      </div>
      {alert.isOverdue && (
        <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs font-medium rounded">OVERDUE</span>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-violet-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400">Loading portfolio data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <p className="text-red-400">{error}</p>
          <button onClick={fetchPortfolioData} className="mt-4 px-4 py-2 bg-violet-600 text-white rounded-lg">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 p-6" data-testid="msp-portfolio-page">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Client Portfolio</h1>
                <p className="text-slate-400">MSP Dashboard - {portfolio?.totalClients || 0} clients</p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/proposals')}
              className="flex items-center gap-2 px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white hover:bg-slate-700 transition-colors"
              data-testid="view-proposals-btn"
            >
              <FileText className="w-4 h-4" />
              Proposals
            </button>
            <button
              onClick={fetchPortfolioData}
              className="p-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Portfolio Stats */}
      <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
        <StatCard icon={Building2} label="Total Clients" value={portfolio?.totalClients || 0} color="violet" />
        <StatCard icon={Shield} label="Avg Compliance" value={`${portfolio?.avgComplianceScore || 0}%`} color="blue" />
        <StatCard 
          icon={AlertTriangle} 
          label="Critical Risk" 
          value={portfolio?.criticalRisk || 0} 
          color="red"
          subValue={`${portfolio?.highRisk || 0} high risk`}
        />
        <StatCard icon={Target} label="Avg Risk Score" value={portfolio?.avgRiskScore || 0} color="amber" />
        <StatCard icon={HardDrive} label="Total Assets" value={portfolio?.totalAssets || 0} color="cyan" />
        <StatCard icon={AlertCircle} label="Open Vulns" value={portfolio?.totalVulnerabilities || 0} color="orange" />
      </div>

      {/* Risk Distribution */}
      <div className="max-w-7xl mx-auto mb-8">
        <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
          <h3 className="text-lg font-semibold text-white mb-4">Risk Distribution</h3>
          <div className="flex items-center gap-4">
            <div className="flex-1 h-4 bg-slate-700 rounded-full overflow-hidden flex">
              {portfolio?.totalClients > 0 && (
                <>
                  <div 
                    className="h-full bg-red-500" 
                    style={{ width: `${(portfolio.criticalRisk / portfolio.totalClients) * 100}%` }}
                  ></div>
                  <div 
                    className="h-full bg-orange-500" 
                    style={{ width: `${(portfolio.highRisk / portfolio.totalClients) * 100}%` }}
                  ></div>
                  <div 
                    className="h-full bg-amber-500" 
                    style={{ width: `${(portfolio.mediumRisk / portfolio.totalClients) * 100}%` }}
                  ></div>
                  <div 
                    className="h-full bg-emerald-500" 
                    style={{ width: `${(portfolio.lowRisk / portfolio.totalClients) * 100}%` }}
                  ></div>
                </>
              )}
            </div>
            <div className="flex items-center gap-4 text-sm">
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-500"></span> Critical ({portfolio?.criticalRisk || 0})</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-orange-500"></span> High ({portfolio?.highRisk || 0})</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-500"></span> Medium ({portfolio?.mediumRisk || 0})</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-500"></span> Low ({portfolio?.lowRisk || 0})</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="flex items-center gap-2 border-b border-slate-700">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'overview' 
                ? 'text-violet-400 border-b-2 border-violet-400' 
                : 'text-slate-400 hover:text-white'
            }`}
          >
            Client Overview
          </button>
          <button
            onClick={() => setActiveTab('alerts')}
            className={`px-4 py-3 text-sm font-medium transition-colors flex items-center gap-2 ${
              activeTab === 'alerts' 
                ? 'text-violet-400 border-b-2 border-violet-400' 
                : 'text-slate-400 hover:text-white'
            }`}
          >
            Critical Alerts
            {alerts.length > 0 && (
              <span className="px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">{alerts.length}</span>
            )}
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto">
        {activeTab === 'overview' && (
          <>
            {/* Search and Filter */}
            <div className="flex flex-col md:flex-row gap-4 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search clients..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-violet-500"
                  data-testid="search-input"
                />
              </div>
              <div className="flex items-center gap-2">
                <Filter className="w-5 h-5 text-slate-400" />
                <select
                  value={filterRisk}
                  onChange={(e) => setFilterRisk(e.target.value)}
                  className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-violet-500"
                  data-testid="risk-filter"
                >
                  <option value="all">All Risk Levels</option>
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>
            </div>

            {/* Clients Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {filteredClients.length > 0 ? (
                filteredClients.map((client) => (
                  <ClientCard key={client.id} client={client} />
                ))
              ) : (
                <div className="col-span-full text-center py-12">
                  <Building2 className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">No clients found matching your criteria</p>
                </div>
              )}
            </div>
          </>
        )}

        {activeTab === 'alerts' && (
          <div className="space-y-4">
            {alerts.length > 0 ? (
              alerts.map((alert, index) => (
                <AlertItem key={index} alert={alert} />
              ))
            ) : (
              <div className="text-center py-12">
                <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
                <p className="text-slate-400">No critical alerts across your portfolio</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MSPPortfolio;
